import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * The placeholder shown where a list, table or panel has nothing to display.
 *
 * `title` and `description` are inputs rather than projected content because
 * an empty state is worth constraining: the legacy console has a dozen
 * hand-rolled variants with inconsistent wording weight and spacing. Any
 * action belongs in the projected content, so a caller can drop in whatever
 * `bz-button` the situation calls for:
 *
 * ```html
 * <bz-empty-state title="No deliveries today" description="Upload a file to get started">
 *   <bz-button tone="secondary">Upload deliveries</bz-button>
 * </bz-empty-state>
 * ```
 *
 * The artwork above the copy is a **generic illustration by default** — an
 * inline SVG drawn from the colour tokens, so it themes with everything else
 * and costs no network request. That is the point of the default: a screen
 * that has nothing to show should not also have to ship an illustration to
 * look finished. A feature with its own artwork overrides it with `image`:
 *
 * ```html
 * <bz-empty-state
 *   image="/assets/no-deliveries.svg"
 *   imageAlt="An empty delivery van"
 *   title="No deliveries today"
 * />
 * ```
 *
 * Sizing is a CSS concern rather than an input: override
 * `--bz-empty-state-image-size` on the host where a panel is too small for the
 * default 7.5rem.
 */
@Component({
  selector: 'bz-empty-state',
  templateUrl: './bz-empty-state.html',
  styleUrl: './bz-empty-state.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzEmptyState {
  readonly title = input.required<string>();
  readonly description = input('');

  /**
   * Artwork to show above the copy. Left unset, the built-in generic
   * illustration is drawn instead.
   *
   * A plain `<img>` rather than `NgOptimizedImage`, for the reason given in
   * `bz-avatar`: the directive throws on `data:` and `blob:` URLs, which is
   * how artwork generated by a consuming application arrives.
   */
  readonly image = input<string | undefined>(undefined);

  /**
   * Alternative text for `image`. Empty by default, which marks the artwork
   * decorative — the right answer whenever the illustration only restates the
   * title, and the reason the generic illustration is `aria-hidden` outright.
   */
  readonly imageAlt = input('');
}
